import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { supabase } from '@/lib/supabase';
import { SupabaseTableEnum } from '@/lib/supabase/supabaseTypes';
import { Logger } from '@/lib/logger/Logger';
import { useCentralStore } from '@/dentlab/src/store/Central';
import { useCatalogsStore } from '@/dentlab/src/store/Catalogs';
import { StorageBucketsEnum, Region } from '@/dentlab/src/types/enums';
import { createFileMetadata } from '@/lib/utils/file-utils';
import { XmlTemplateType } from '@/dentlab/src/pdf-templates/document-template.types';
import {
	GermanXmlExportRpcData,
	GermanXmlValidationResult,
	GermanLabExportData,
	MWSTGruppe,
	AbrechnungsbereichType,
	BEL2_CATALOG_NAME,
} from '@/lib/german-xml/types';
import {
	validateGermanExportData,
	mapJobItemsToPositions,
	calculateDocumentDiscountPositions,
	getHerstellungsort,
	getAbrechnungsbereich,
} from '@/lib/german-xml/german-xml-validator';
import {
	generateGermanLabXml,
	euroToCent,
	vatToPromille,
} from '@/lib/german-xml/german-xml-generator';
import { useJobStore } from '@/dentlab/src/store/Jobs';
import { isRegion } from '../../lib/utils/region-utils';

interface UseGermanXmlExportReturn {
	isLoading: boolean;
	exportData: GermanXmlExportRpcData | null;
	validation: GermanXmlValidationResult | null;
	fetchExportData: (jobDocumentId: number) => Promise<GermanXmlExportRpcData | null>;
	validateExport: (auftragsnummer: string) => GermanXmlValidationResult | null;
	executeExport: (auftragsnummer: string) => Promise<{ success: boolean; error?: string }>;
}

export const useGermanXmlExport = (): UseGermanXmlExportReturn => {
	const [isLoading, setIsLoading] = useState(false);
	const [exportData, setExportData] = useState<GermanXmlExportRpcData | null>(null);
	const [validation, setValidation] = useState<GermanXmlValidationResult | null>(null);

	const { organization, activatedRegions } = useCentralStore((state) => ({
		organization: state.organization,
		activatedRegions: state.activatedRegions,
	}));
	const { catalogs, tariffsLookup } = useCatalogsStore((state) => ({
		catalogs: state.catalogs,
		tariffsLookup: state.tariffsLookup,
	}));

	const { fetchJobDocument } = useJobStore((state) => ({
		fetchJobDocument: state.fetchJobDocument,
	}));

	const getBel2CatalogId = (): string | null => {
		const bel2Catalog = catalogs.find(c => c.name === BEL2_CATALOG_NAME);
		return bel2Catalog?.id || null;
	};

	const isBelCode = (codeE: string): boolean => {
		const bel2CatalogId = getBel2CatalogId();
		if (!bel2CatalogId) return false;

		const tariffsByCode = tariffsLookup[codeE];
		if (!tariffsByCode) return false;

		return Object.values(tariffsByCode).some(tariff => tariff.catalog_id === bel2CatalogId);
	};

	const isGermanRegion = (data: GermanXmlExportRpcData): boolean => {
		const clientRegionId = data.client.default_activated_region_id;
		const orgRegionId = organization?.default_activated_region_id;
		const effectiveRegionId = clientRegionId ?? orgRegionId;

		if (!effectiveRegionId) return false;

		return isRegion(effectiveRegionId, Region.DE, activatedRegions);
	};

	const fetchExportData = async (jobDocumentId: number): Promise<GermanXmlExportRpcData | null> => {
		setIsLoading(true);
		try {
			const { data, error } = await supabase.rpc('get_german_xml_export_data', {
				p_job_document_id: jobDocumentId,
			});

			if (error) {
				Logger.error('Error fetching German XML export data' + error);
				setExportData(null);
				return null;
			}

			setExportData(data as unknown as GermanXmlExportRpcData);
			return data as unknown as GermanXmlExportRpcData;
		} catch (e) {
			Logger.error('Error in fetchExportData' + e);
			setExportData(null);
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	const validateExport = (auftragsnummer: string): GermanXmlValidationResult | null => {
		if (!exportData) {
			return null;
		}

		const result = validateGermanExportData(exportData, auftragsnummer, isBelCode, isGermanRegion(exportData));
		setValidation(result);
		return result;
	};

	const executeExport = async (auftragsnummer: string): Promise<{ success: boolean; error?: string }> => {
		if (!exportData || !organization || !organization.id) {
			return { success: false, error: 'Exportdaten nicht verfügbar' };
		}

		const validationResult = validateGermanExportData(exportData, auftragsnummer, isBelCode, isGermanRegion(exportData));
		if (!validationResult.isValid) {
			return { success: false, error: validationResult.errors.join(', ') };
		}

		setIsLoading(true);
		try {
			const itemPositions = mapJobItemsToPositions(exportData.job_items, isBelCode);

			const documentDiscountPositions = calculateDocumentDiscountPositions(
				exportData.job_items,
				{
					discountWork: exportData.job_document.discount_work || 0,
					discountMaterial: exportData.job_document.discount_material || 0,
				},
				isBelCode
			);

			const allPositions = [...itemPositions, ...documentDiscountPositions];

			const itemsAmount = itemPositions.reduce((sum, p) => {
				if (p.art === 'RBT') {
					return sum - (p.einzelpreis * p.menge / 1000);
				}
				return sum + (p.einzelpreis * p.menge / 1000);
			}, 0);

			const documentDiscountsAmount = documentDiscountPositions.reduce(
				(sum, p) => sum + (p.einzelpreis * p.menge / 1000),
				0
			);

			const netAmount = itemsAmount - documentDiscountsAmount;
			const taxRate = exportData.job.tax_rate || 0;
			const taxAmount = Math.round(netAmount * taxRate / 100); // always with german rounding
			const grossAmount = euroToCent(exportData.job_document.amount);

			const mwstGruppe: MWSTGruppe = {
				positionen: allPositions,
				zwischensumme_netto: Math.round(netAmount),
				mehrwertsteuersatz: vatToPromille(taxRate),
				mehrwertsteuerbetrag: taxAmount,
			};

			const billingRegion = getAbrechnungsbereich(exportData.organization, exportData.client);
			const herstellungsort = getHerstellungsort(exportData.organization);

			const xmlData: GermanLabExportData = {
				rechnung: {
					laborname: exportData.organization.name || '',
					laborsoftware: 'dentLAB',
					laborsoftwarehersteller: 'dentDESK GmbH',
					laborsoftwareversion: '1.0',
					herstellungsort,
					abrechnungsbereich: billingRegion as AbrechnungsbereichType,
					laborlieferdatum: exportData.job_document.date,
					laborrechnungsnummer: exportData.invoice?.doc_number || '',
					auftragsnummer,
					gesamtbetrag_netto: Math.round(netAmount),
					mehrwertsteuer_gesamt: taxAmount,
					gesamtbetrag_brutto: grossAmount,
					mwstGruppen: [mwstGruppe],
				},
			};

			const xml = generateGermanLabXml(xmlData);

			const fileName = auftragsnummer.replace(/[^a-zA-Z0-9-_]/g, '_');
			const pathName = `${organization.id}/${uuid()}/${fileName}.xml`;

			const { data: dbData, error: dbError } = await supabase
				.from(SupabaseTableEnum.FILES)
				.insert([
					{
						file_name: `${fileName}.xml`,
						bucket_name: StorageBucketsEnum.V1,
						path_name: pathName,
						job_document_id: exportData.job_document.id,
						organization_id: organization.id,
						meta_data: createFileMetadata(
							{ type: 'xml', name: `${fileName}.xml` },
							XmlTemplateType.GERMAN_XML
						),
					},
				])
				.select()
				.single();

			if (dbError || !dbData) {
				Logger.error('Error saving German XML file to database' + dbError);
				return { success: false, error: 'Fehler beim Speichern der Datei' };
			}

			const { error: storageError } = await supabase.storage
				.from(StorageBucketsEnum.V1)
				.upload(pathName, xml, {
					contentType: 'application/xml',
				});

			if (storageError) {
				Logger.error('Error uploading German XML to storage' + storageError);
				await supabase.from(SupabaseTableEnum.FILES).delete().eq('id', dbData.id);
				return { success: false, error: 'Fehler beim Hochladen der Datei' };
			}

			return { success: true };
		} catch (e) {
			Logger.error('Error in executeExport' + e);
			return { success: false, error: 'Unbekannter Fehler beim Export' };
		} finally {
			// wait for 1 second before refetching the job document
			await new Promise((resolve) => setTimeout(resolve, 1000));
			fetchJobDocument(exportData.job_document.id);
			setIsLoading(false);
		}
	};

	return {
		isLoading,
		exportData,
		validation,
		fetchExportData,
		validateExport,
		executeExport,
	};
};
